import { createSlice } from "@reduxjs/toolkit";

const SESSION_DURATION_MS = 60 * 60 * 1000; // 1 hour

function loadAuthFromStorage() {
  try {
    const raw =
      localStorage.getItem("auth") || sessionStorage.getItem("auth");
    if (!raw) return { user: null, token: "", refreshToken: "", expiresAt: null };
    const parsed = JSON.parse(raw);
    if (parsed.expiresAt && Date.now() > parsed.expiresAt) {
      localStorage.removeItem("auth");
      sessionStorage.removeItem("auth");
      return { user: null, token: "", refreshToken: "", expiresAt: null };
    }
    return {
      user: parsed.user || null,
      token: parsed.token || "",
      refreshToken: parsed.refreshToken || "",
      expiresAt: parsed.expiresAt || null,
    };
  } catch {
    return { user: null, token: "", refreshToken: "", expiresAt: null };
  }
}

function persist(state) {
  const authData = {
    user: state.user,
    token: state.token,
    refreshToken: state.refreshToken,
    expiresAt: state.expiresAt,
  };
  if (localStorage.getItem("auth") !== null) {
    localStorage.setItem("auth", JSON.stringify(authData));
  } else {
    sessionStorage.setItem("auth", JSON.stringify(authData));
  }
}

const initialState = loadAuthFromStorage();

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCredentials(state, action) {
      const { user, token, refreshToken = "", rememberMe } = action.payload;
      state.user = user;
      state.token = token;
      state.refreshToken = refreshToken;
      state.expiresAt = Date.now() + SESSION_DURATION_MS;

      const authData = {
        user,
        token,
        refreshToken,
        expiresAt: state.expiresAt,
      };
      if (rememberMe) {
        localStorage.setItem("auth", JSON.stringify(authData));
      } else {
        sessionStorage.setItem("auth", JSON.stringify(authData));
      }
    },
    updateAccessToken(state, action) {
      // Called after a silent refresh — only replace access token + expiry
      state.token = action.payload.token;
      state.expiresAt = Date.now() + SESSION_DURATION_MS;
      persist(state);
    },
    clearCredentials(state) {
      state.user = null;
      state.token = "";
      state.refreshToken = "";
      state.expiresAt = null;
      localStorage.removeItem("auth");
      sessionStorage.removeItem("auth");
    },
    refreshExpiry(state) {
      if (!state.token) return;
      state.expiresAt = Date.now() + SESSION_DURATION_MS;
      persist(state);
    },
  },
});

export const { setCredentials, updateAccessToken, clearCredentials, refreshExpiry } =
  authSlice.actions;

export const selectAuth = (state) => state.auth;
export const selectUser = (state) => state.auth.user;
export const selectToken = (state) => state.auth.token;
export const selectRefreshToken = (state) => state.auth.refreshToken;
export const selectExpiresAt = (state) => state.auth.expiresAt;

export default authSlice.reducer;
